import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  const poll = useCallback(async (attempt) => {
    if (attempt > 8) { setState("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refreshUser().catch(() => {});
        setState("paid");
        return;
      }
    } catch { setState("error"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, refreshUser]);

  useEffect(() => { if (sessionId) poll(0); else setState("error"); }, [sessionId, poll]);

  return (
    <div className="min-h-screen wo-bg flex items-center justify-center px-5">
      <div className="liquid-glass rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (<>
          <Loader2 size={48} className="mx-auto text-teal-400 animate-spin mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-100">Confirming your subscription…</h1>
          <p className="text-slate-400 text-sm mt-2">This only takes a moment.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={56} className="mx-auto text-teal-400 mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-100" data-testid="payment-success-title">You're all set!</h1>
          <p className="text-slate-400 text-sm mt-2">Your plan is active. Time to fill your roles faster.</p>
          <Link to="/dashboard"><Button data-testid="payment-success-continue" className="mt-6 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Go to dashboard</Button></Link>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <XCircle size={56} className="mx-auto text-amber-400 mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-100">Still processing</h1>
          <p className="text-slate-400 text-sm mt-2">Your payment may take a little longer to confirm. Refresh your dashboard shortly.</p>
          <Link to="/dashboard"><Button className="mt-6 bg-white/5 border border-white/15 text-slate-100">Go to dashboard</Button></Link>
        </>)}
      </div>
    </div>
  );
}
